import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, BookOpen, Search } from 'lucide-react';
import { CardImage } from '@/components/CardImage';
import { useCards, type Card } from '@/hooks/useCards';
import { useCollection } from '@/hooks/useCollection';
import type { InkColor } from '@/lib/ink-utils';

type Preset = 'master' | 'enchanted' | 'all' | 'custom';

const PAGE_SIZE = 9;

function sortByNumber(cards: Card[]): Card[] {
  return [...cards].sort((a, b) => {
    const na = parseInt(a.set_number || '', 10);
    const nb = parseInt(b.set_number || '', 10);
    const aNum = isNaN(na) ? Infinity : na;
    const bNum = isNaN(nb) ? Infinity : nb;
    if (aNum !== bNum) return aNum - bNum;
    return (a.set_number || '').localeCompare(b.set_number || '');
  });
}

export function BinderView() {
  const { data: allCards, isLoading } = useCards({});
  const { data: collection } = useCollection();
  const [selectedSet, setSelectedSet] = useState<string>('');
  const [preset, setPreset] = useState<Preset>('master');
  const [customSearch, setCustomSearch] = useState('');
  const [pageIdx, setPageIdx] = useState(0);

  const ownedCardIds = useMemo(
    () => new Set((collection || []).map(c => c.card_id)),
    [collection]
  );

  const sets = useMemo(() => {
    if (!allCards) return [];
    const s = new Set<string>();
    allCards.forEach(c => s.add(c.set_name));
    return Array.from(s).sort();
  }, [allCards]);

  // Default to first set when loaded
  if (sets.length > 0 && !selectedSet) {
    setSelectedSet(sets[0]);
  }

  const filtered = useMemo(() => {
    if (!allCards) return [];
    let cards = allCards;
    if (preset !== 'custom') {
      if (!selectedSet) return [];
      cards = cards.filter(c => c.set_name === selectedSet);
      if (preset === 'enchanted') cards = cards.filter(c => c.enchanted);
      else if (preset === 'master') cards = cards.filter(c => !c.enchanted);
    } else {
      const s = customSearch.trim().toLowerCase();
      if (s) {
        cards = cards.filter(c =>
          c.name.toLowerCase().includes(s) ||
          (c.subtitle || '').toLowerCase().includes(s)
        );
      }
    }
    return sortByNumber(cards);
  }, [allCards, selectedSet, preset, customSearch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(pageIdx, totalPages - 1);
  const pageCards = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const ownedInBinder = filtered.filter(c => ownedCardIds.has(c.id)).length;

  const onSetChange = (val: string) => { setSelectedSet(val); setPageIdx(0); };
  const onPresetChange = (val: Preset) => { setPreset(val); setPageIdx(0); };

  return (
    <div className="mt-3 space-y-3">
      {/* Header */}
      <div className="rounded-xl border border-border bg-card p-3 space-y-3">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary" />
          <h3 className="font-display text-sm font-semibold text-foreground">Binder Mode</h3>
        </div>
        <p className="text-[10px] text-muted-foreground font-body">
          Visualize a set as binder pages. Owned cards appear in full color, missing ones are greyed out.
        </p>

        {/* Set picker */}
        <div>
          <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Set</p>
          <select
            value={selectedSet}
            onChange={e => onSetChange(e.target.value)}
            aria-label="Choose binder set"
            className="w-full rounded-lg border border-border bg-secondary px-2.5 py-2 text-xs text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {sets.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Preset chips */}
        <div>
          <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Preset</p>
          <div className="flex flex-wrap gap-1.5">
            {([['master', 'Master Set'], ['enchanted', 'Enchanteds'], ['all', 'All Printings'], ['custom', 'Custom']] as [Preset, string][]).map(([val, label]) => (
              <button
                key={val}
                onClick={() => onPresetChange(val)}
                className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${preset === val ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom search */}
        {preset === 'custom' && (
          <div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Search Cards</p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                value={customSearch}
                onChange={e => { setCustomSearch(e.target.value); setPageIdx(0); }}
                placeholder="Search card name..."
                aria-label="Search cards for custom binder"
                className="w-full rounded-lg border border-border bg-secondary py-2 pl-9 pr-3 text-xs text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
        )}

        {/* Progress */}
        {filtered.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-muted-foreground font-body">{ownedInBinder}/{filtered.length} owned</span>
              <span className="text-[10px] text-primary font-body">{Math.round((ownedInBinder / filtered.length) * 100)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500"
                style={{ width: `${(ownedInBinder / filtered.length) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Binder page */}
      {isLoading ? (
        <div className="py-12 text-center text-xs text-muted-foreground font-body">Loading binder…</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-4xl mb-3">📖</p>
          <p className="text-sm text-muted-foreground font-body">No cards in this preset</p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border-2 border-border bg-gradient-to-br from-card to-secondary p-3 shadow-inner">
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => {
                const card = pageCards[i];
                if (!card) {
                  return <div key={i} className="aspect-[2/3] rounded-lg border border-dashed border-border/50 bg-background/40" />;
                }
                const owned = ownedCardIds.has(card.id);
                return (
                  <div key={card.id} className="relative">
                    <CardImage
                      src={card.image_url}
                      alt={card.name}
                      inkColor={card.ink_color as InkColor}
                      enchanted={card.enchanted || false}
                      showGlow={owned}
                      className={`aspect-[2/3] transition-all ${owned ? '' : 'grayscale opacity-30'}`}
                    />
                    {card.set_number && (
                      <span className="absolute bottom-1 right-1 rounded bg-background/80 px-1 py-0.5 text-[8px] font-mono text-foreground">
                        #{card.set_number}
                      </span>
                    )}
                    {!owned && (
                      <span className="absolute top-1 left-1 rounded bg-background/80 px-1 py-0.5 text-[8px] font-body text-muted-foreground">
                        Missing
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pager */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setPageIdx(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              aria-label="Previous page"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-foreground disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs text-muted-foreground font-body">
              Page {safePage + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPageIdx(Math.min(totalPages - 1, safePage + 1))}
              disabled={safePage >= totalPages - 1}
              aria-label="Next page"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-foreground disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
